import React, { memo, Suspense, useEffect, useMemo, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import Loader from "@Components/Loader";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import useImpressionOnce from "hooks/useImpressionOnce";
import { PAGE } from "constants/configConstants";
import config from "@Config/index";
import useIsHappyFlow from "hooks/useIsHappyFlow";
import SearchListItem from "../SearchListItem";
import SearchListItemDesktopV2 from "../SearchListItem/SearchListItemDesktopV2";
import TrustPilotReviewsBanner from "../SearchList/components/TrustPilotBanner";
import BookEarlySection from "../BookEarlySection";
import LazyLoadOnScroll from "@Components/LazyLoadOnScroll";
import {
  buildListCPlan,
  resolveBreakerViewMode,
  shouldShowAfterCard,
  shouldShowGetHelpBeforeCard,
  type PageBreakerState,
  type ViewMode,
} from "../SearchList/filteredBreakerPlacement";
import { setRecommendationsInView } from "../../data/searchPageReducer";
import useRecommendations from "./useRecommendations";
import { FILTERS } from "../FilterSection/data/filtersList";
import { APPLIED_FILTERS, SEPARATOR } from "../../utils/recommendationCopy";
import { KEPT_GROUP_KEYS } from "../../utils/filterTokens";
import styles from "./recommendedBeyondFilters.module.scss";

/** Group id → filter tab label (an existing Tolgee key), so the copy names match the tabs. */
// Housing Type and Housing Fest have no plain-name translation, so the always-kept filters
// use their panel translation keys.
const FILTER_LABEL_BY_KEY: Record<string, string> = Object.fromEntries(
  FILTERS.map(({ key, name, translationKey }) => [
    key,
    KEPT_GROUP_KEYS.includes(key) ? translationKey : name,
  ]),
);

const GetHelpBannerV2 = React.lazy(() => import("../SearchList/components/GetHelpBannerV2"));

type Props = {
  /** Desktop list view renders the desktop card; grid and mobile render the mobile card. */
  isDesktop?: boolean;
  isGridView?: boolean;
  /** Desktop map sidebar: one column, and the map pins these properties while they are seen. */
  isMapView?: boolean;
  useFilteredBreakers?: boolean;
  breakerPageState?: PageBreakerState;
  breakerViewMode?: ViewMode;
};

const RecommendedBeyondFilters = ({
  isDesktop = false,
  isGridView = false,
  isMapView = false,
  useFilteredBreakers = false,
  breakerPageState,
  breakerViewMode = "mobileList",
}: Props) => {
  const { t } = useTranslationHook();
  const dispatch = useDispatch();
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const {
    visible,
    items,
    pageLoading,
    mainItemsLength,
    onSectionSeen,
    cardContextFor,
    listCTotal,
    copy,
    appliedFiltersLine,
  } = useRecommendations({ isDesktop: isDesktop || isGridView });

  const isHappyFlow = useIsHappyFlow();
  const { region, customLocation, whiteLabelPartnerData, getHelpClicked } = useSelector(
    ({ searchPageData, userHistory }: AppState) => ({
      region: searchPageData.region,
      customLocation: searchPageData.customLocation,
      whiteLabelPartnerData: searchPageData.whiteLabelPartnerData,
      getHelpClicked: userHistory.getHelpClicked,
    }),
    shallowEqual,
  );

  const getHelpDismissedForCity = Boolean(
    isHappyFlow && region?.canonical_name && getHelpClicked.includes(region.canonical_name),
  );

  const listCMap = useMemo(() => {
    if (!useFilteredBreakers || !breakerPageState || listCTotal <= 0) return null;
    const viewMode = resolveBreakerViewMode(breakerViewMode, isGridView, isDesktop);
    return buildListCPlan({
      listCTotal,
      pageState: breakerPageState,
      ctx: {
        viewMode,
        isChinaApp: Boolean(config.IS_CHINA_APP),
        getHelpDismissedForCity,
      },
    }).listC;
  }, [
    useFilteredBreakers,
    breakerPageState,
    listCTotal,
    breakerViewMode,
    isGridView,
    isDesktop,
    getHelpDismissedForCity,
  ]);

  useImpressionOnce(headerRef, onSectionSeen, { enabled: visible });

  useEffect(() => {
    const el = sectionRef.current;
    if (!isMapView || !el || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(([entry]) =>
      dispatch(setRecommendationsInView(entry.isIntersecting)),
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      dispatch(setRecommendationsInView(false));
    };
  }, [isMapView, visible, dispatch]);

  if (!visible) return null;

  const labelFor = (group: string) =>
    t(FILTER_LABEL_BY_KEY[group] ?? group, undefined, { dynamic: true });
  // No summary (state hydrated from an older build): today's heading and subheading.
  const heading = t(copy?.headingKey ?? "searchpage.recommendations.heading");
  // "Applied filters: <applied> · +N · <removed, struck through>".
  const lineItems = appliedFiltersLine
    ? [
        ...appliedFiltersLine.applied.map((item) => ({
          text: "key" in item ? t(item.key) : labelFor(item.group),
          removed: false,
        })),
        ...(appliedFiltersLine.moreCount
          ? [{ text: `+${appliedFiltersLine.moreCount}`, removed: false }]
          : []),
        ...appliedFiltersLine.removed.map((group) => ({ text: labelFor(group), removed: true })),
      ]
    : [];

  const Card = isDesktop ? SearchListItemDesktopV2 : SearchListItem;
  const isDesktopListView = isDesktop && !isGridView;

  const wrapGridBreaker = (node: React.ReactNode) =>
    isGridView ? <div className={styles.fullWidthRow}>{node}</div> : node;

  const renderListCBreakersAfter = (localIndex: number) => {
    if (!listCMap) return null;
    const showTrustPilot =
      shouldShowAfterCard(listCMap, localIndex, "trustpilot") && !config.IS_CHINA_APP;
    const showBookEarly =
      isDesktopListView && shouldShowAfterCard(listCMap, localIndex, "bookEarly");
    const showGetHelpMobile =
      !isDesktopListView && shouldShowAfterCard(listCMap, localIndex, "getHelp");
    // Match SearchList / SearchListDesktop Trustpilot props — not homepage styles on desktop list.
    const trustPilotIsDesktop = isGridView || isDesktopListView;
    const trustPilotIsHomePage = !isGridView && !isDesktopListView;

    return (
      <>
        {showGetHelpMobile &&
          wrapGridBreaker(
            <LazyLoadOnScroll
              noLazy={isGridView}
              fallback={
                <section style={{ height: "180px" }} id="GetHelpBannerV2-recommendations" />
              }
            >
              <GetHelpBannerV2 isDesktop={isGridView} />
            </LazyLoadOnScroll>,
          )}
        {showTrustPilot &&
          wrapGridBreaker(
            <TrustPilotReviewsBanner
              uniCity={null}
              isSearchPage
              isHomePage={trustPilotIsHomePage}
              isDesktop={trustPilotIsDesktop}
            />,
          )}
        {showBookEarly && wrapGridBreaker(<BookEarlySection isDesktop />)}
      </>
    );
  };

  const renderListCBreakersBefore = (localIndex: number) => {
    if (!listCMap || !isDesktopListView) return null;
    if (!shouldShowGetHelpBeforeCard(listCMap, localIndex)) return null;
    return wrapGridBreaker(
      <Suspense
        fallback={
          <section style={{ height: "180px" }} id="GetHelpBannerV2-recommendations-before" />
        }
      >
        <GetHelpBannerV2 isDesktop />
      </Suspense>,
    );
  };

  return (
    <section
      ref={sectionRef}
      className={styles.section}
      aria-labelledby="search-recommendations-heading"
      data-testid="search-recommendations"
    >
      <div ref={headerRef} className={styles.header}>
        <h2 id="search-recommendations-heading" className={styles.heading}>
          {heading}
        </h2>
        {appliedFiltersLine ? (
          <p className={styles.subheading}>
            <span className={styles.appliedLabel}>{t(APPLIED_FILTERS)}</span>{" "}
            {lineItems.map(({ text, removed }, i) => (
              <React.Fragment key={`${text}-${i}`}>
                {i > 0 && <span className={styles.separator}>{SEPARATOR}</span>}
                {removed ? (
                  <s className={styles.removedFilter}>{text}</s>
                ) : (
                  <span className={styles.appliedFilter}>{text}</span>
                )}
              </React.Fragment>
            ))}
          </p>
        ) : (
          <p className={styles.subheading}>{t("searchpage.recommendations.subheading")}</p>
        )}
      </div>

      <div
        className={formatClassNames(
          isGridView ? styles.cardsGrid : styles.cards,
          isMapView && styles.mapView,
        )}
      >
        {items.map((item: any, i: number) => (
          <React.Fragment key={item.id}>
            {renderListCBreakersBefore(i)}
            <Card
              index={mainItemsLength + i}
              {...item}
              regionType={region?.region_type}
              regionId={region?.id}
              region={region}
              meta={item.meta_short}
              meta_real={item.meta}
              page={PAGE.SEARCH}
              isGridView={isGridView}
              customLocation={customLocation}
              whiteLabelPartnerData={whiteLabelPartnerData}
              recommendation={cardContextFor(item, i)}
            />
            {renderListCBreakersAfter(i)}
          </React.Fragment>
        ))}
      </div>

      {pageLoading && (
        <div className={styles.loader} aria-hidden="true">
          <Loader noShadow />
        </div>
      )}
    </section>
  );
};

export default wrapperHOC(memo(RecommendedBeyondFilters), {
  componentName: "RecommendedBeyondFilters",
  showForChina: true,
  showForWhiteLable: false,
  showForApp: true,
  showForMiniProgram: false,
  height: "0px",
});
